import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  ENABLED_ANALYTICS_ROUTE,
  ENABLED_COMPANY_OFFERS_ROUTE,
  ENABLED_CUSTOMERS_ROUTE,
  ENABLED_INSIGHTS_ROUTE,
  ENABLED_MARKETING_ROUTE,
} from '@constants/company-auth-permissions';
import { XS_VIEWPORT } from '@constants/viewport';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import { useGetCompany } from '@hooks/company/use-get-company';
import { useIsProtectedElement } from '@hooks/use-is-protected-element';
import { useWindowSize } from '@hooks/use-window-size';
import { getMainSideBarMenu, MAIN_MENU_FIELDS } from '@pages/events/utils/main-sidebar-menu';

export const useMainSidebarMenu = () => {
  const { i18n } = useTranslation();
  const { clubState, companyState } = useContext(CompanyClubContext);
  const { data: company } = useGetCompany({
    companyId: companyState.legacyId,
    clubId: clubState.id,
    options: { enabled: !!companyState.legacyId && !!clubState.id },
  });
  const { isWebView } = useContext(FeatureFlagContext);
  const isMobileViewport = useWindowSize(XS_VIEWPORT);
  const location = useLocation();

  const isAnalyticsEnabled = useIsProtectedElement({ permissions: ENABLED_ANALYTICS_ROUTE });
  const isMarketingEnabled = useIsProtectedElement({ permissions: ENABLED_MARKETING_ROUTE });
  const isInsightsEnabled = useIsProtectedElement({ permissions: ENABLED_INSIGHTS_ROUTE });
  const isOffersEnabled = useIsProtectedElement({ permissions: ENABLED_COMPANY_OFFERS_ROUTE });
  const isCustomersEnabled = useIsProtectedElement({ permissions: ENABLED_CUSTOMERS_ROUTE });

  const companyId = companyState.legacyId;
  const clubSlug = clubState.slug;
  const citySlug = clubState.city.slug;
  const hasBI = !!company?.lookerStudioUrl;
  const lang = i18n.language || 'en';

  const current = useMemo(() => {
    if (location.pathname.includes('/analytics')) {
      return MAIN_MENU_FIELDS.ANALYTICS;
    } else if (location.pathname.includes('/events')) {
      return MAIN_MENU_FIELDS.EVENTS;
    } else if (location.pathname.includes('/customers')) {
      return MAIN_MENU_FIELDS.CUSTOMERS;
    } else if (location.pathname.includes('/offers')) {
      return MAIN_MENU_FIELDS.OFFERS;
    } else if (location.pathname.includes('/insights')) {
      return MAIN_MENU_FIELDS.INSIGHTS;
    } else if (location.pathname.includes('/marketing')) {
      return MAIN_MENU_FIELDS.MARKETING;
    } else {
      return null;
    }
  }, [location.pathname]);

  return useMemo(() => {
    if (!current) return null;
    const menu = getMainSideBarMenu({
      clubSlug,
      citySlug,
      companyId,
      current,
      lang,
      hasBI,
      isDesktop: !isMobileViewport,
      isWebView,
    });

    return menu.filter((item) => {
      switch (item.name) {
        case MAIN_MENU_FIELDS.ANALYTICS:
          return isAnalyticsEnabled;
        case MAIN_MENU_FIELDS.CUSTOMERS:
          return isCustomersEnabled;
        case MAIN_MENU_FIELDS.OFFERS:
          return isOffersEnabled;
        case MAIN_MENU_FIELDS.INSIGHTS:
          return isInsightsEnabled;
        case MAIN_MENU_FIELDS.MARKETING:
          return isMarketingEnabled;
        default:
          return true;
      }
    });
  }, [
    clubSlug,
    citySlug,
    companyId,
    current,
    lang,
    hasBI,
    isMobileViewport,
    isWebView,
    isAnalyticsEnabled,
    isCustomersEnabled,
    isOffersEnabled,
    isInsightsEnabled,
    isMarketingEnabled,
  ]);
};
